/**
 * WASM Loader for AgentShield in Edge Runtime
 * Handles loading and initialization of WebAssembly module
 */

// Dynamic WASM loading - no direct imports to avoid build issues
let wasmInstance: WebAssembly.Instance | null = null;
let wasmExports: any = null;
let initPromise: Promise<void> | null = null;

// Store the WASM module URL for runtime loading
const WASM_PATH = '/wasm/agentshield_wasm_bg.wasm';

// Store base URL for constructing absolute URLs in Edge Runtime
let baseUrl: string | null = null;

/**
 * Set the base URL for WASM loading (needed in Edge Runtime)
 */
export function setWasmBaseUrl(url: string) {
  baseUrl = url;
}

/**
 * Construct the full WASM URL based on context
 */
function getWasmUrl(): string {
  // If we have a base URL (Edge Runtime), construct absolute URL
  if (baseUrl) {
    try {
      const url = new URL(baseUrl);
      // Use the origin to construct the full URL
      return `${url.origin}${WASM_PATH}`;
    } catch {
      // Fallback to relative path if URL construction fails
      return WASM_PATH;
    }
  }
  // Default to relative path
  return WASM_PATH;
}

/**
 * Initialize the WASM module
 * This is safe to call multiple times - it will only initialize once
 */
export async function initWasm(): Promise<boolean> {
  if (wasmExports) return true;
  if (initPromise) {
    await initPromise;
    return !!wasmExports;
  }

  initPromise = (async () => {
    try {
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000); // 3 second timeout
      
      try {
        const wasmUrl = getWasmUrl();
        
        // Try streaming compilation first (most efficient)
        if (typeof WebAssembly.instantiateStreaming === 'function') {
          try {
            const response = await fetch(wasmUrl, { signal: controller.signal });
            clearTimeout(timeout);
            
            if (!response.ok) {
              throw new Error(`Failed to fetch WASM: ${response.status}`);
            }
            
            // Clone response for streaming (in case we need to retry)
            const streamResponse = response.clone();
            
            // Use streaming compilation for best performance
            const { instance } = await WebAssembly.instantiateStreaming(
              streamResponse,
              {
                wbg: {
                  __wbg_log_1d3ae13c3d5e6b8e: (ptr: number, len: number) => {
                    console.log('WASM:', ptr, len);
                  },
                  __wbindgen_throw: (ptr: number, len: number) => {
                    throw new Error(`WASM Error at ${ptr}, length ${len}`);
                  }
                }
              }
            );
            
            wasmInstance = instance;
            wasmExports = instance.exports;
            
            console.log('[AgentShield] ✅ WASM module initialized with streaming');
            return;
          } catch (streamError) {
            // If not aborted, try fallback
            if (!controller.signal.aborted) {
              console.log('[AgentShield] Streaming compilation failed, falling back to standard compilation');
            } else {
              throw streamError;
            }
          }
        }
        
        // Fallback to standard compilation if streaming not available
        const response = await fetch(wasmUrl, { signal: controller.signal });
        clearTimeout(timeout);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch WASM: ${response.status}`);
        }
        
        const wasmArrayBuffer = await response.arrayBuffer();
        const compiledModule = await WebAssembly.compile(wasmArrayBuffer);
        
        const imports = {
          wbg: {
            __wbg_log_1d3ae13c3d5e6b8e: (ptr: number, len: number) => {
              console.log('WASM:', ptr, len);
            },
            __wbindgen_throw: (ptr: number, len: number) => {
              throw new Error(`WASM Error at ${ptr}, length ${len}`);
            }
          }
        };
        
        wasmInstance = await WebAssembly.instantiate(compiledModule, imports);
        wasmExports = wasmInstance.exports;
        
        console.log('[AgentShield] ✅ WASM module initialized via fallback');
      } catch (fetchError: any) {
        if (fetchError.name === 'AbortError') {
          console.warn('[AgentShield] WASM fetch timed out after 3 seconds - using pattern detection');
        } else {
          console.warn('[AgentShield] Failed to fetch WASM file:', fetchError.message);
        }
        wasmExports = null;
      }
    } catch (error) {
      console.error('[AgentShield] Failed to initialize WASM:', error);
      wasmExports = null;
    }
  })();

  await initPromise;
  return !!wasmExports;
}

/**
 * Detect agent using WASM
 * Returns null if WASM is not available
 */
export async function detectAgentWithWasm(
  userAgent: string | undefined,
  headers: Record<string, string>,
  ipAddress?: string
): Promise<{
  isAgent: boolean;
  confidence: number;
  agent?: string;
  verificationMethod: string;
  riskLevel: string;
} | null> {
  // Ensure WASM is initialized
  const initialized = await initWasm();
  if (!initialized || !wasmExports) {
    return null;
  }

  try {
    // Note: The actual WASM exports will depend on how wasm-bindgen generates them
    // This is a simplified version - you may need to adjust based on actual exports
    
    // Prepare input for WASM
    const headersJson = JSON.stringify(headers);
    
    // Call WASM detection function (adjust function name based on actual exports)
    if (typeof wasmExports.detect_agent === 'function') {
      const result = wasmExports.detect_agent(
        userAgent || '',
        headersJson,
        ipAddress || '',
        new Date().toISOString()
      );
      
      // Parse result (format depends on WASM implementation)
      return {
        isAgent: result.is_agent || false,
        confidence: result.confidence || 0,
        agent: result.agent,
        verificationMethod: result.verification_method || 'wasm',
        riskLevel: result.risk_level || 'low',
      };
    }
    
    console.warn('[AgentShield] WASM exports do not include detect_agent function');
    return null;
  } catch (error) {
    console.error('[AgentShield] WASM detection failed:', error);
    return null;
  }
}

/**
 * Get WASM version
 */
export async function getWasmVersion(): Promise<string | null> {
  const initialized = await initWasm();
  if (!initialized || !wasmExports) {
    return null;
  }
  
  if (typeof wasmExports.version === 'function') {
    return wasmExports.version();
  }
  
  return 'unknown';
}

/**
 * Check if WASM is available and working
 */
export async function isWasmAvailable(): Promise<boolean> {
  try {
    const initialized = await initWasm();
    if (!initialized) return false;
    
    // Try to get version as a simple test
    const version = await getWasmVersion();
    return version !== null;
  } catch {
    return false;
  }
}